import React, { useEffect, useId, useState } from 'react'
import { useAuth } from '../auth'
import { ApiError, api } from '../lib/api'
import { inputClass } from './Field'

type Mode = 'sign-in' | 'sign-up' | 'forgot' | 'reset'

/** Matches the server's minimum, so the form can say so before submitting. */
const MIN_PASSWORD_LENGTH = 10

/**
 * The one screen you see signed out: sign in, create an account, ask for a
 * reset link, or set a new password after clicking one.
 *
 * These are four small forms rather than four routes because the app has no
 * router — the deep links that have to work arrive as query parameters this
 * reads on mount: the reset link (?token=) and the website's sign-up button
 * (?signup), which opens on the create-account form.
 */
export default function AuthScreen() {
  const { signIn, signUp, notice: authNotice } = useAuth()
  const [mode, setMode] = useState<Mode>(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.has('token')) return 'reset'
    return params.has('signup') ? 'sign-up' : 'sign-in'
  })
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [notice, setNotice] = useState<string | null>(authNotice)

  const resetToken = new URLSearchParams(window.location.search).get('token') ?? ''
  const emailHelpId = useId()
  const passwordHelpId = useId()

  // Someone who clicked a confirmation link while signed out lands here; say
  // what happened rather than showing a bare login form.
  useEffect(() => {
    const verify = new URLSearchParams(window.location.search).get('verify')
    if (verify === 'ok') setNotice('Email confirmed. Sign in to carry on.')
    if (verify === 'already') setNotice('That address was already confirmed.')
    if (verify === 'expired') setNotice('That confirmation link has expired. Sign in and ask for another.')
    if (verify === 'invalid') setNotice("That confirmation link didn't look right. Sign in and ask for another.")
  }, [])

  function switchTo(next: Mode) {
    setMode(next)
    setError(null)
    setFieldErrors({})
    setNotice(null)
  }

  async function run(action: () => Promise<void>) {
    setBusy(true)
    setError(null)
    setFieldErrors({})
    try {
      await action()
    } catch (e) {
      if (e instanceof ApiError) {
        setError(e.message)
        setFieldErrors(e.fields ?? {})
      } else {
        setError('Something went wrong. Try again.')
      }
    } finally {
      setBusy(false)
    }
  }

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (busy) return

    if (mode === 'sign-in') return void run(() => signIn(email, password))
    if (mode === 'sign-up') return void run(() => signUp(email, password))
    if (mode === 'forgot') {
      return void run(async () => {
        const { message } = await api.forgotPassword(email)
        setNotice(message)
      })
    }
    return void run(async () => {
      await api.resetPassword(resetToken, password)
      // Drop the token from the address bar before anything else — a reset
      // link sitting in history is worth removing even though it is now spent.
      window.history.replaceState({}, '', window.location.pathname)
      setNotice('Password changed. Sign in with it below.')
      setPassword('')
      setMode('sign-in')
    })
  }

  const title = {
    'sign-in': 'Sign in',
    'sign-up': 'Create an account',
    forgot: 'Reset your password',
    reset: 'Choose a new password',
  }[mode]

  const submitLabel = {
    'sign-in': 'Sign in',
    'sign-up': 'Create account',
    forgot: 'Send reset link',
    reset: 'Save new password',
  }[mode]

  return (
    <div className="min-h-screen flex flex-col items-center px-4 py-10">
      <div className="w-full max-w-sm my-auto">
        <h1 className="font-display text-3xl text-ink text-center">Snapshot Money</h1>
        <p className="text-sm text-ink/70 text-center mt-2 mb-8">Your income, bills and budgets, day by day.</p>

        <div className="bg-white border border-line rounded p-5 sm:p-6">
          <h2 className="font-display text-xl text-ink mb-4">{title}</h2>

          {notice && (
            <p className="mb-4 text-sm text-accent bg-accent-soft border border-accent/20 rounded-sm px-3 py-2">
              {notice}
            </p>
          )}
          {error && (
            <p className="mb-4 text-sm text-rag-bad bg-rag-badBg border border-rag-bad/20 rounded-sm px-3 py-2">
              {error}
            </p>
          )}

          <form onSubmit={onSubmit} noValidate>
            {/* Errors and hints sit outside each <label> and are tied to the
                field as its description, so they aren't read out as part of
                the field's name. */}
            {mode !== 'reset' && (
              <div className="mb-4">
                <label className="block">
                  <span className="block text-xs font-sans text-ink/75 mb-1">Email</span>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                    autoCapitalize="none"
                    autoCorrect="off"
                    required
                    aria-invalid={fieldErrors.email ? true : undefined}
                    aria-describedby={fieldErrors.email ? emailHelpId : undefined}
                    // 16px keeps iOS Safari from zooming the whole page in when
                    // the field takes focus.
                    className={`${inputClass} text-base py-2`}
                  />
                </label>
                {fieldErrors.email && (
                  <span id={emailHelpId} className="block text-xs text-rag-bad mt-1">
                    {fieldErrors.email}
                  </span>
                )}
              </div>
            )}

            {mode !== 'forgot' && (
              <div className="mb-4">
                <label className="block">
                  <span className="block text-xs font-sans text-ink/75 mb-1">Password</span>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete={mode === 'sign-in' ? 'current-password' : 'new-password'}
                    required
                    minLength={mode === 'sign-in' ? undefined : MIN_PASSWORD_LENGTH}
                    aria-invalid={fieldErrors.password ? true : undefined}
                    aria-describedby={passwordHelpId}
                    className={`${inputClass} text-base py-2`}
                  />
                </label>
                <span id={passwordHelpId}>
                  {fieldErrors.password && (
                    <span className="block text-xs text-rag-bad mt-1">{fieldErrors.password}</span>
                  )}
                  {(mode === 'sign-up' || mode === 'reset') && !fieldErrors.password && (
                    <span className="block text-[11px] text-ink/70 mt-1">
                      At least {MIN_PASSWORD_LENGTH} characters. A short phrase beats a mangled word.
                    </span>
                  )}
                </span>
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              className="w-full bg-accent text-white rounded-sm py-2.5 text-sm font-sans hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {busy ? 'Working…' : submitLabel}
            </button>
            {mode === 'sign-up' && (
              <p className="text-xs text-ink/70 mt-3">
                By creating an account you agree to the{' '}
                <a href="/terms" className="text-accent underline hover:no-underline">
                  terms of use
                </a>
                . Our{' '}
                <a href="/privacy" className="text-accent underline hover:no-underline">
                  privacy policy
                </a>{' '}
                explains how we look after your data.
              </p>
            )}
          </form>

          <div className="mt-5 pt-4 border-t border-line text-sm text-ink/75 space-y-2">
            {mode === 'sign-in' && (
              <>
                <p>
                  No account yet?{' '}
                  <button onClick={() => switchTo('sign-up')} className="text-accent hover:underline">
                    Create one
                  </button>
                </p>
                <p>
                  <button onClick={() => switchTo('forgot')} className="text-accent hover:underline">
                    Forgotten your password?
                  </button>
                </p>
              </>
            )}
            {mode !== 'sign-in' && (
              <p>
                <button onClick={() => switchTo('sign-in')} className="text-accent hover:underline">
                  Back to sign in
                </button>
              </p>
            )}
          </div>
        </div>

        <p className="text-xs text-center mt-6 space-x-4">
          <a href="/privacy" className="text-ink/70 hover:text-accent underline">
            Privacy policy
          </a>
          <a href="/terms" className="text-ink/70 hover:text-accent underline">
            Terms of use
          </a>
        </p>
      </div>
    </div>
  )
}
